import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardBody, Badge, Button, Input } from '@img/ui';
import { api, type ApiSuccess, type Paginated } from '../../lib/api';
import { ImageUploader } from '../../components/ImageUploader';

interface Association {
  id: string;
  type: string;
  name: string;
  slug: string;
  status: string;
  city: string | null;
  district: string | null;
  totalUnits: number;
  totalBuildings: number;
  currentBalance: number | string;
  logo: string | null;
  themeColor: string | null;
  company: { id: string; name: string };
  _count?: { members: number; units: number; maintenanceTickets: number };
}

interface Company {
  id: string;
  name: string;
}

const TYPE_LABEL: Record<string, string> = {
  SINGLE_BUILDING: 'مبنى واحد',
  COMPLEX: 'مجمع',
  FEDERATION: 'اتحاد',
  DEVELOPER_INTERIM: 'مؤقتة',
  NEIGHBORHOOD: 'حي',
};
const STATUS_LABEL: Record<string, { label: string; color: 'green' | 'amber' | 'red' | 'navy' }> = {
  DRAFT:        { label: 'مسودة', color: 'amber' },
  PENDING_REGA: { label: 'قيد التسجيل في REGA', color: 'amber' },
  ACTIVE:       { label: 'فعّالة', color: 'green' },
  SUSPENDED:    { label: 'موقوفة', color: 'red' },
  DISSOLVED:    { label: 'منحلّة', color: 'red' },
};

export function AdminAssociationsPage() {
  const qc = useQueryClient();
  const [filters, setFilters] = useState({ q: '', type: '', status: '', city: '', companyId: '' });
  const [openCreate, setOpenCreate] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'associations', filters],
    queryFn: () => api.get<Paginated<Association>>('/api/associations', { query: { ...filters, limit: 50 } }),
  });

  const companies = useQuery({
    queryKey: ['management-companies'],
    queryFn: () => api.get<ApiSuccess<Company[]>>('/api/admin/management-companies'),
  });

  const deleteM = useMutation({
    mutationFn: (id: string) => api.delete(`/api/associations/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'associations'] }),
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="text-3xl font-black text-navy-dark">🏛️ جمعيات الملاك</h1>
        <Button variant="primary" onClick={() => setOpenCreate(true)}>+ جمعية جديدة</Button>
      </div>

      <Card className="mb-4">
        <CardBody>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <Input placeholder="بحث…" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} />
            <select value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })} className="px-4 py-2.5 rounded-xl border-2 border-gray-200 bg-white text-sm">
              <option value="">كل الأنواع</option>
              {Object.entries(TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="px-4 py-2.5 rounded-xl border-2 border-gray-200 bg-white text-sm">
              <option value="">كل الحالات</option>
              {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <Input placeholder="المدينة" value={filters.city} onChange={(e) => setFilters({ ...filters, city: e.target.value })} />
            <select value={filters.companyId} onChange={(e) => setFilters({ ...filters, companyId: e.target.value })} className="px-4 py-2.5 rounded-xl border-2 border-gray-200 bg-white text-sm">
              <option value="">كل الشركات</option>
              {companies.data?.data?.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        </CardBody>
      </Card>

      {isLoading ? (
        <div className="text-gray-500">⟳ جاري التحميل…</div>
      ) : !data?.data?.length ? (
        <Card><CardBody>
          <div className="text-center py-12 text-gray-500">
            <div className="text-5xl mb-3">🏛️</div>
            <div>لا توجد جمعيات بعد</div>
          </div>
        </CardBody></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {data.data.map((a) => (
            <Card key={a.id}>
              <CardBody>
                <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                  <div className="flex items-center gap-3">
                    {a.logo
                      ? <img src={a.logo} alt={a.name} className="w-12 h-12 rounded-full object-cover" />
                      : <div className="w-12 h-12 rounded-full flex items-center justify-center text-2xl" style={{ background: a.themeColor ?? '#1e3a5f', color: '#fff' }}>🏛️</div>
                    }
                    <div>
                      <h3 className="font-black text-navy-dark text-lg">{a.name}</h3>
                      <p className="text-xs text-gray-500">{TYPE_LABEL[a.type]} · {a.city ?? '—'}</p>
                    </div>
                  </div>
                  <Badge color={STATUS_LABEL[a.status]?.color ?? 'navy'}>{STATUS_LABEL[a.status]?.label ?? a.status}</Badge>
                </div>
                <div className="text-xs text-gray-500 mb-2">شركة: <b>{a.company.name}</b></div>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <Stat label="مباني" value={String(a.totalBuildings)} />
                  <Stat label="وحدات" value={`${a._count?.units ?? 0}/${a.totalUnits}`} />
                  <Stat label="أعضاء" value={String(a._count?.members ?? 0)} />
                </div>
                <div className="flex gap-1 mt-3 pt-3 border-t border-gray-100">
                  <Link to={`/admin/associations/${a.id}`} className="flex-1">
                    <Button size="sm" variant="primary" className="w-full">إدارة</Button>
                  </Link>
                  <a href={`/associations/${a.slug}`} target="_blank" rel="noreferrer">
                    <Button size="sm" variant="ghost">🔗</Button>
                  </a>
                  <Button size="sm" variant="ghost" onClick={() => { if (confirm(`حذف ${a.name}؟ لا يمكن التراجع.`)) deleteM.mutate(a.id); }}>🗑</Button>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      {openCreate && (
        <CreateAssociationModal
          companies={companies.data?.data ?? []}
          onClose={() => setOpenCreate(false)}
          onCreated={() => qc.invalidateQueries({ queryKey: ['admin', 'associations'] })}
        />
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return <div className="bg-gray-50 rounded-lg p-2"><div className="text-gray-500">{label}</div><div className="font-bold text-sm">{value}</div></div>;
}

function CreateAssociationModal({ companies, onClose, onCreated }: { companies: Company[]; onClose: () => void; onCreated: () => void }) {
  const [f, setF] = useState({
    companyId: companies[0]?.id ?? '',
    type: 'SINGLE_BUILDING',
    name: '',
    nameEn: '',
    crNumber: '',
    regaRegistration: '',
    city: '',
    district: '',
    address: '',
    totalBuildings: '1',
    totalUnits: '',
    bankName: '',
    bankIban: '',
    themeColor: '#1e3a5f',
    logo: '',
    coverImage: '',
  });
  const [err, setErr] = useState('');

  // 🔍 فحص الحقول الإلزامية مع رسائل عربية واضحة
  const validate = (): string | null => {
    const missing: string[] = [];
    if (!f.companyId) missing.push('شركة الإدارة');
    if (!f.name?.trim() || f.name.trim().length < 2) missing.push('اسم الجمعية (حرفان على الأقل)');
    if (!f.totalUnits || Number(f.totalUnits) <= 0) missing.push('إجمالي الوحدات (رقم موجب)');
    if (!f.totalBuildings || Number(f.totalBuildings) <= 0) missing.push('عدد المباني (رقم موجب)');
    if (missing.length > 0) {
      return `الرجاء تعبئة: ${missing.join(' • ')}`;
    }
    return null;
  };

  const m = useMutation({
    mutationFn: () => {
      const e = validate();
      if (e) throw new Error(e);
      return api.post<ApiSuccess<{ id: string; slug: string }>>('/api/associations', {
        ...f,
        totalBuildings: Number(f.totalBuildings),
        totalUnits: Number(f.totalUnits),
        establishmentDate: new Date().toISOString(),
      });
    },
    onSuccess: () => { onCreated(); onClose(); },
    onError: (e: Error) => setErr(e.message),
  });

  return (
    <div className="fixed inset-0 bg-black/60 z-50 overflow-y-auto p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-auto my-4" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b flex items-center justify-between">
          <h2 className="text-xl font-black text-navy-dark">➕ جمعية ملاك جديدة</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl">✕</button>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-bold text-ink mb-1">شركة الإدارة *</label>
            <select value={f.companyId} onChange={(e) => setF({ ...f, companyId: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 bg-white">
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-bold text-ink mb-1">النوع *</label>
            <select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 bg-white">
              <option value="SINGLE_BUILDING">جمعية مبنى واحد</option>
              <option value="COMPLEX">مجمع/كومباوند</option>
              <option value="FEDERATION">اتحاد جمعيات</option>
              <option value="DEVELOPER_INTERIM">جمعية مؤقتة (مطوّر)</option>
              <option value="NEIGHBORHOOD">جمعية حي</option>
            </select>
          </div>
          <Input label="اسم الجمعية *" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
          <Input label="الاسم بالإنجليزي" value={f.nameEn} onChange={(e) => setF({ ...f, nameEn: e.target.value })} dir="ltr" />
          <Input label="رقم تسجيل REGA" value={f.regaRegistration} onChange={(e) => setF({ ...f, regaRegistration: e.target.value })} />
          <Input label="السجل التجاري" value={f.crNumber} onChange={(e) => setF({ ...f, crNumber: e.target.value })} />
          <Input label="المدينة" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} />
          <Input label="الحي" value={f.district} onChange={(e) => setF({ ...f, district: e.target.value })} />
          <div className="md:col-span-2">
            <Input label="العنوان" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} />
          </div>
          <Input label="عدد المباني *" type="number" value={f.totalBuildings} onChange={(e) => setF({ ...f, totalBuildings: e.target.value })} />
          <Input label="إجمالي الوحدات *" type="number" value={f.totalUnits} onChange={(e) => setF({ ...f, totalUnits: e.target.value })} />
          <Input label="البنك" value={f.bankName} onChange={(e) => setF({ ...f, bankName: e.target.value })} />
          <Input label="IBAN" value={f.bankIban} onChange={(e) => setF({ ...f, bankIban: e.target.value })} dir="ltr" placeholder="SA00..." />
          <div>
            <label className="block text-sm font-bold text-ink mb-1">لون الواجهة</label>
            <input type="color" value={f.themeColor} onChange={(e) => setF({ ...f, themeColor: e.target.value })} className="w-full h-12 rounded-xl border-2 border-gray-200 cursor-pointer" />
          </div>

          <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t">
            <ImageUploader
              value={f.logo}
              onChange={(url) => setF({ ...f, logo: url ?? '' })}
              purpose="ASSOCIATION_LOGO"
              label="🏛️ لوقو الجمعية"
              hint="مربع — حد أقصى 2MB"
              shape="square"
              size="md"
            />
            <ImageUploader
              value={f.coverImage}
              onChange={(url) => setF({ ...f, coverImage: url ?? '' })}
              purpose="ASSOCIATION_COVER"
              label="🖼️ صورة الغلاف"
              hint="عريضة — حد أقصى 5MB"
              shape="wide"
            />
          </div>
        </div>
        {err && <div className="mx-5 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">⛔ {err}</div>}
        <div className="p-5 border-t flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>إلغاء</Button>
          <Button variant="primary" onClick={() => m.mutate()} loading={m.isPending}>💾 إنشاء</Button>
        </div>
      </div>
    </div>
  );
}
